import { useCallback, useEffect, useState } from "react";
import { Plus, Cog } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSite } from "../lib/site";
import { PageHeader } from "../components/ModulePage";
import { FormDialog } from "../components/FormDialog";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { RolesPanel } from "../components/RolesPanel";
import { MachineAssign } from "../components/MachineAssign";

const FIELDS = [
  { name: "name", label: "Full name", type: "text", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "company", label: "Company (contractors)", type: "text" },
  { name: "password", label: "Temporary password", type: "text", required: true },
];

export default function Users() {
  const { user: me } = useAuth();
  const { sites, site } = useSite();
  const [roles, setRoles] = useState([]);
  const [assign, setAssign] = useState(null);
  const loadRoles = useCallback(() => api.get("/roles").then((r) => setRoles(r.data.roles)), []);
  useEffect(() => { loadRoles(); }, [loadRoles]);
  const roleOpts = roles.filter((r) => r.key !== "admin" || me.role === "admin").map((r) => [r.key, r.label]);
  const fields = [...FIELDS.slice(0, 2), { name: "role", label: "Role", type: "select", options: roleOpts, required: true }, ...FIELDS.slice(2), { name: "site_ids", label: "Site access", type: "checks", options: sites.map((s) => [s.id, s.name]) }];
  const toggleSite = (u, sid) => patch(u, { site_ids: (u.site_ids || []).includes(sid) ? u.site_ids.filter((x) => x !== sid) : [...(u.site_ids || []), sid] });
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/users").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const create = async (v) => {
    try {
      await api.post("/users", v);
      toast.success("User created");
      load();
    } catch (e) { toast.error(fmtErr(e)); throw e; }
  };
  const patch = async (u, body) => {
    try {
      await api.patch(`/users/${u.id}`, body);
      toast.success("User updated");
      load();
      loadRoles();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Administration" title="Users & Roles" description="Create accounts, choose what each role can see and do in every module, and assign machine checks to people.">
        <Button onClick={() => setOpen(true)} className="tap bg-rose-600 hover:bg-rose-700" data-testid="add-user-btn"><Plus className="mr-1 h-4 w-4" />Add user</Button>
      </PageHeader>
      <Tabs defaultValue="users">
        <TabsList className="mb-4 bg-slate-900">
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Roles & Permissions</TabsTrigger>
        </TabsList>
        <TabsContent value="roles"><RolesPanel onChange={loadRoles} /></TabsContent>
        <TabsContent value="users">
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-800 text-left"><tr>{["Name", "Email", "Role", "Company", "Sites", "Machine checks", "Active"].map((h) => <th key={h} className="eyebrow px-3 py-3 font-normal">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-b border-slate-800/70" data-testid={`user-row-${u.email}`}>
                <td className="px-3 py-2">{u.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                <td className="px-3 py-2">
                  <select className="field h-9" value={u.role} disabled={u.id === me.id} onChange={(e) => patch(u, { role: e.target.value })} data-testid={`user-role-${u.email}`}>
                    {roleOpts.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    {!roleOpts.some(([k]) => k === u.role) && <option value={u.role}>{u.role}</option>}
                  </select>
                </td>
                <td className="px-3 py-2">{u.company || "-"}</td>
                <td className="px-3 py-2">
                  {u.role === "admin" ? <span className="text-xs text-slate-400">All sites</span> : (
                    <div className="flex flex-wrap gap-1">
                      {sites.map((s) => (
                        <button key={s.id} onClick={() => toggleSite(u, s.id)} data-testid={`user-site-${u.email}-${s.id}`}
                          className={`rounded border px-2 py-1 text-[11px] ${(u.site_ids || []).includes(s.id) ? "border-rose-500 bg-rose-500/20 text-rose-100" : "border-slate-700 text-slate-500"}`}>{s.name}</button>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Button size="sm" variant="outline" className="h-9 whitespace-nowrap border-slate-700 bg-transparent text-xs" onClick={() => setAssign(u)} data-testid={`user-machines-${u.email}`}>
                    <Cog className="mr-1 h-3.5 w-3.5" />{(u.machine_sections || []).length || (u.machine_ids || []).length ? [...(u.machine_sections || []), ...((u.machine_ids || []).length ? [`+${u.machine_ids.length} machine(s)`] : [])].join(", ") : "Assign"}
                  </Button>
                </td>
                <td className="px-3 py-2"><input type="checkbox" className="h-5 w-5 accent-rose-600" disabled={u.id === me.id} checked={u.active} onChange={(e) => patch(u, { active: e.target.checked })} data-testid={`user-active-${u.email}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </TabsContent>
      </Tabs>
      <MachineAssign user={assign} onClose={() => setAssign(null)} onSaved={load} />
      <FormDialog open={open} onOpenChange={setOpen} title="New user" fields={fields} initial={{ role: "operator", site_ids: site ? [site.id] : [] }} onSubmit={create} testid="user-form" />
    </div>
  );
}
